import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { 
  Database,
  Users, 
  FileText, 
  TrendingUp, 
  Activity,
  BarChart3,
  Truck,
  Package,
  Award,
  AlertTriangle,
  Sparkles,
  Target,
  Shield,
  Lightbulb,
  TrendingDown,
  CheckCircle,
  Filter,
  X
} from 'lucide-react';

// Styled Components
const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.5s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryDark} 100%);
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const HeaderSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  opacity: 0.9;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div<{ color: string }>`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-left: 4px solid ${({ color }) => color};
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  background: ${({ color }) => color}15;
  color: ${({ color }) => color};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const StatSubtext = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const SectionContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

// Styled Components para Filtros
const FiltersSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FiltersTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const FilterButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.white : theme.colors.primary};
  color: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.textPrimary : theme.colors.white};
  border: ${({ theme, variant }) => variant === 'secondary' ? `2px solid ${theme.colors.gray300}` : 'none'};
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.xl}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme, variant }) => variant === 'secondary' ? theme.colors.gray50 : theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FiltrosActivos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FiltroTag = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`;

const ListItemText = styled.div`
  flex: 1;
`;

const ListItemLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2px;
`;

const ListItemSubtext = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ListItemValue = styled.div<{ color?: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ color, theme }) => color || theme.colors.primary};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}15;
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Styled Components para Tabla de Ranking
const RankingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const TableHeader = styled.thead`
  background: rgba(255, 255, 255, 0.1);
  
  th {
    padding: ${({ theme }) => theme.spacing.md};
    text-align: left;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &:not(:first-child) {
      text-align: right;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    transition: background 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: ${({ theme }) => theme.spacing.md};
    color: rgba(255, 255, 255, 0.9);
    
    &:not(:first-child) {
      text-align: right;
    }
  }
`;

const Badge = styled.span<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  background: ${({ color }) => color || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: white;
`;

const DestacadosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DestacadoCard = styled.div<{ color: string }>`
  background: ${({ color }) => color}20;
  border: 2px solid ${({ color }) => color};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  
  h4 {
    margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ color }) => color};
  }
`;

const AnalysisSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: white;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const AnalysisHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const AnalysisTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const GenerateButton = styled.button`
  background: white;
  color: #667eea;
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const AnalysisContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const AnalysisBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AnalysisBlockTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: rgba(255, 255, 255, 0.95);
`;

const AnalysisText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AnalysisList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const AnalysisListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  
  &:before {
    content: "•";
    color: rgba(255, 255, 255, 0.7);
    font-weight: bold;
    font-size: 1.2em;
    margin-top: -2px;
  }
`;

const AnalysisTimestamp = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
`;

// Interfaces
interface EstadisticasDashboard {
  total_rutas: number;
  total_oferentes: number;
  total_ofertas: number;
  rutas_con_ofertas: number;
  porcentaje_cobertura: number;
  proveedores_activos: Array<{
    proveedor: string;
    ofertas: number;
  }>;
  rutas_mas_cotizadas: Array<{
    ruta: string;
    proveedores: number;
  }>;
  distribucion_vehiculos: Array<{
    tipo: string;
    total: number;
  }>;
  distribucion_cargas: Array<{
    tipo: string;
    total: number;
  }>;
  rutas_sin_ofertas: Array<{
    origen: string;
    destino: string;
    tipo_vehiculo: string;
    tipo_carga: string;
    configuracion: string;
    valor_sicetac: number;
    ruta: string;
  }>;
}

interface AnalisisGemini {
  resumen_ejecutivo: string;
  diagnostico_general: string[];
  oportunidades: string[];
  riesgos: string[];
  recomendaciones_estrategicas: string[];
  insights_competitivos: string[];
  proyecciones: string[];
}

interface ProveedorInfo {
  nombre: string;
  nit: string;
  username: string;
  estado: string;
  contacto: {
    nombre: string;
    celular: string;
    correo: string;
  };
  experiencia: {
    anios: string;
    transporte_alimentos: boolean;
    ha_trabajado_alianza: boolean;
  };
  flota: {
    numero_vehiculos: number;
    porcentaje_propia: string;
    edad_promedio: string;
    tecnologias: string;
    capacidades_carga: string;
    tipos_vehiculo: string;
  };
  tecnologia: {
    gps_sistema: string;
    capacitacion_conductores: boolean;
    capacitacion_carga_alimentos: boolean;
    medicion_huella_carbono: boolean;
  };
  certificaciones: string;
  ultima_milla: {
    tiene_capacidad: boolean;
    ciudades: string;
  };
  fecha_registro: string;
  estadisticas_cotizaciones: {
    rutas_cotizadas: number;
    total_cotizaciones: number;
    promedio_oferta: number;
    oferta_minima: number;
    oferta_maxima: number;
  };
}

const Dashboard: React.FC = () => {
  const { proveedor } = useAuth();
  const [estadisticas, setEstadisticas] = useState<EstadisticasDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analisisGemini, setAnalisisGemini] = useState<AnalisisGemini | null>(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [analisisTimestamp, setAnalisisTimestamp] = useState<string | null>(null);
  const [proveedorInfo, setProveedorInfo] = useState<ProveedorInfo | null>(null); // Nueva información del proveedor
  const [analisisProveedorTexto, setAnalisisProveedorTexto] = useState<string | null>(null); // Análisis con IA del proveedor
  const [loadingAnalisisProveedor, setLoadingAnalisisProveedor] = useState(false);
  
  // Estados para ranking de proveedores
  const [rankingProveedores, setRankingProveedores] = useState<any[]>([]);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [destacados, setDestacados] = useState<any>(null);
  
  // Estados para filtros
  const [filtroOrigenCiudad, setFiltroOrigenCiudad] = useState('');
  const [filtroDestinoCiudad, setFiltroDestinoCiudad] = useState('');
  const [filtroTipoVehiculo, setFiltroTipoVehiculo] = useState('');
  const [filtroTipoCarga, setFiltroTipoCarga] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  
  // Opciones para los filtros (listas de valores únicos)
  const [opcionesOrigenes, setOpcionesOrigenes] = useState<string[]>([]);
  const [opcionesDestinos, setOpcionesDestinos] = useState<string[]>([]);
  const [opcionesTiposVehiculo, setOpcionesTiposVehiculo] = useState<string[]>([]);
  const [opcionesTiposCarga, setOpcionesTiposCarga] = useState<string[]>([]);
  const [opcionesProveedores, setOpcionesProveedores] = useState<string[]>([]);

  // Función para cargar estadísticas (con o sin filtros)
  const cargarEstadisticas = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construir query params con filtros (SIN proveedor)
      const params = new URLSearchParams();
      if (filtroOrigenCiudad) params.append('origen_ciudad', filtroOrigenCiudad);
      if (filtroDestinoCiudad) params.append('destino_ciudad', filtroDestinoCiudad);
      if (filtroTipoVehiculo) params.append('tipo_vehiculo', filtroTipoVehiculo);
      if (filtroTipoCarga) params.append('tipo_carga', filtroTipoCarga);
      
      const queryString = params.toString();
      const url = `/admin/dashboard/estadisticas${queryString ? '?' + queryString : ''}`;
      
      console.log('🔍 Cargando estadísticas con filtros:', url);
      
      const response = await api.get(url);
      setEstadisticas(response.data);
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      setError('Error al cargar las estadísticas del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar ranking de proveedores (con filtros)
  const cargarRankingProveedores = async () => {
    try {
      setLoadingRanking(true);
      
      // Construir query params con filtros (sin incluir filtro de proveedor)
      const params = new URLSearchParams();
      if (filtroOrigenCiudad) params.append('origen_ciudad', filtroOrigenCiudad);
      if (filtroDestinoCiudad) params.append('destino_ciudad', filtroDestinoCiudad);
      if (filtroTipoVehiculo) params.append('tipo_vehiculo', filtroTipoVehiculo);
      if (filtroTipoCarga) params.append('tipo_carga', filtroTipoCarga);
      
      const queryString = params.toString();
      const url = `/admin/dashboard/ranking-proveedores${queryString ? '?' + queryString : ''}`;
      
      console.log('📊 Cargando ranking de proveedores:', url);
      
      const response = await api.get(url);
      setRankingProveedores(response.data.ranking || []);
      setDestacados({
        mejor_proveedor: response.data.mejor_proveedor,
        mas_caro: response.data.mas_caro,
        mas_competitivo: response.data.mas_competitivo
      });
    } catch (error) {
      console.error('Error obteniendo ranking de proveedores:', error);
    } finally {
      setLoadingRanking(false);
    }
  };

  // Cargar opciones de filtros al inicio
  useEffect(() => {
    // Solo cargar datos si es ADMINISTRADOR
    if (proveedor?.username !== 'ADMINISTRADOR') {
      setLoading(false);
      return;
    }

    const fetchOpcionesFiltros = async () => {
      try {
        const response = await api.get('/admin/dashboard/filtros-opciones');
        setOpcionesOrigenes(response.data.origenes || []);
        setOpcionesDestinos(response.data.destinos || []);
        setOpcionesTiposVehiculo(response.data.tipos_vehiculo || []);
        setOpcionesTiposCarga(response.data.tipos_carga || []);
        setOpcionesProveedores(response.data.proveedores || []);
        console.log('✅ Opciones de filtros cargadas');
      } catch (error) {
        console.error('Error obteniendo opciones de filtros:', error);
      }
    };

    fetchOpcionesFiltros();
    cargarEstadisticas();
    cargarRankingProveedores();
  }, [proveedor]);
  
  // Recargar estadísticas y ranking cuando cambian los filtros (no incluye filtroProveedor)
  useEffect(() => {
    if (proveedor?.username === 'ADMINISTRADOR') {
      cargarEstadisticas();
      cargarRankingProveedores();
    }
  }, [filtroOrigenCiudad, filtroDestinoCiudad, filtroTipoVehiculo, filtroTipoCarga]);
  
  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setFiltroOrigenCiudad('');
    setFiltroDestinoCiudad('');
    setFiltroTipoVehiculo('');
    setFiltroTipoCarga('');
    setFiltroProveedor('');
    setProveedorInfo(null); // Limpiar información del proveedor también
  };

  const generarAnalisisGemini = async () => {
    if (!estadisticas) return;

    try {
      setLoadingAnalisis(true);
      
      // Crear objeto con estadísticas Y filtros aplicados (SIN proveedor)
      const dataParaGemini = {
        ...estadisticas,
        filtros_aplicados: {
          origen_ciudad: filtroOrigenCiudad,
          destino_ciudad: filtroDestinoCiudad,
          tipo_vehiculo: filtroTipoVehiculo,
          tipo_carga: filtroTipoCarga
        }
      };
      
      console.log('🤖 Generando análisis con Gemini. Filtros:', dataParaGemini.filtros_aplicados);
      
      const response = await api.post('/admin/dashboard/analisis-gemini', dataParaGemini);
      setAnalisisGemini(response.data.analisis);
      setAnalisisTimestamp(response.data.timestamp);
    } catch (error) {
      console.error('Error generando análisis con Gemini:', error);
      setError('Error al generar el análisis con IA');
    } finally {
      setLoadingAnalisis(false);
    }
  };
  
  // Función para obtener información detallada de un proveedor
  const obtenerInfoProveedor = async (username: string) => {
    try {
      console.log('📋 Obteniendo información del proveedor:', username);
      const response = await api.get(`/admin/dashboard/proveedor-info/${username}`);
      setProveedorInfo(response.data);
      setFiltroProveedor(username); // Marcar como seleccionado
      setAnalisisProveedorTexto(null); // Limpiar análisis anterior
      console.log('✅ Información del proveedor recibida:', response.data.nombre);
      
      // Actualizar la tarjeta del ranking con los datos reales del proveedor
      const stats = response.data.estadisticas_cotizaciones;
      if (stats && rankingProveedores.length > 0) {
        const rankingActualizado = rankingProveedores.map(prov => {
          if (prov.proveedor === username) {
            return {
              ...prov,
              ofertas: stats.total_cotizaciones || prov.ofertas || 0,
              promedio_oferta: stats.promedio_oferta || prov.promedio_oferta || 0,
              // Si hay promedio de oferta, calcular diferencia con SICETAC
              diferencia_sicetac: prov.diferencia_sicetac !== undefined ? prov.diferencia_sicetac : 0
            };
          }
          return prov;
        });
        setRankingProveedores(rankingActualizado);
      }
    } catch (error) {
      console.error('Error obteniendo información del proveedor:', error);
      setError('Error al cargar la información del proveedor');
    }
  };

  // Función para generar análisis del proveedor con Gemini
  const generarAnalisisProveedor = async () => {
    if (!filtroProveedor) return;
    
    try {
      setLoadingAnalisisProveedor(true);
      console.log('🤖 Generando análisis del proveedor con Gemini:', filtroProveedor);
      const response = await api.post(`/admin/dashboard/proveedor-analisis/${filtroProveedor}`, {});
      setAnalisisProveedorTexto(response.data.analisis);
      console.log('✅ Análisis del proveedor generado exitosamente');
    } catch (error) {
      console.error('Error generando análisis del proveedor:', error);
      setError('Error al generar el análisis del proveedor');
    } finally {
      setLoadingAnalisisProveedor(false);
    }
  };

  // Verificar que el usuario sea ADMINISTRADOR (después de todos los hooks)
  if (proveedor?.username !== 'ADMINISTRADOR') {
    return <Navigate to="/rutas" replace />;
  }

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingSpinner>
          <Activity size={32} style={{ marginRight: '12px', animation: 'spin 2s linear infinite' }} />
          Cargando estadísticas...
        </LoadingSpinner>
      </DashboardContainer>
    );
  }

  if (error || !estadisticas) {
    return (
      <DashboardContainer>
        <ErrorMessage>{error || 'No se pudieron cargar las estadísticas'}</ErrorMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <HeaderTitle>
          <BarChart3 size={40} />
          Panel de Administración
        </HeaderTitle>
        <HeaderSubtitle>
          Vista general del sistema de licitaciones - Bienvenido, ADMINISTRADOR
        </HeaderSubtitle>
      </HeaderSection>

      {/* Sección de Filtros */}
      <FiltersSection>
        <FiltersTitle>
          <Filter size={20} />
          Filtros de Análisis
        </FiltersTitle>
        
        {/* Mostrar filtros activos */}
        {(filtroOrigenCiudad || filtroDestinoCiudad || filtroTipoVehiculo || filtroTipoCarga) && (
          <FiltrosActivos>
            {filtroOrigenCiudad && (
              <FiltroTag>
                Origen: {filtroOrigenCiudad}
                <X size={14} style={{ cursor: 'pointer' }} onClick={() => setFiltroOrigenCiudad('')} />
              </FiltroTag>
            )}
            {filtroDestinoCiudad && (
              <FiltroTag>
                Destino: {filtroDestinoCiudad}
                <X size={14} style={{ cursor: 'pointer' }} onClick={() => setFiltroDestinoCiudad('')} />
              </FiltroTag>
            )}
            {filtroTipoVehiculo && (
              <FiltroTag>
                Vehículo: {filtroTipoVehiculo}
                <X size={14} style={{ cursor: 'pointer' }} onClick={() => setFiltroTipoVehiculo('')} />
              </FiltroTag>
            )}
            {filtroTipoCarga && (
              <FiltroTag>
                Carga: {filtroTipoCarga}
                <X size={14} style={{ cursor: 'pointer' }} onClick={() => setFiltroTipoCarga('')} />
              </FiltroTag>
            )}
          </FiltrosActivos>
        )}
        
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Ciudad Origen</FilterLabel>
            <FilterSelect
              value={filtroOrigenCiudad}
              onChange={(e) => setFiltroOrigenCiudad(e.target.value)}
            >
              <option value="">Todas las ciudades</option>
              {opcionesOrigenes.map((origen) => (
                <option key={origen} value={origen}>{origen}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Ciudad Destino</FilterLabel>
            <FilterSelect
              value={filtroDestinoCiudad}
              onChange={(e) => setFiltroDestinoCiudad(e.target.value)}
            >
              <option value="">Todas las ciudades</option>
              {opcionesDestinos.map((destino) => (
                <option key={destino} value={destino}>{destino}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo de Vehículo</FilterLabel>
            <FilterSelect
              value={filtroTipoVehiculo}
              onChange={(e) => setFiltroTipoVehiculo(e.target.value)}
            >
              <option value="">Todos los vehículos</option>
              {opcionesTiposVehiculo.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo de Carga</FilterLabel>
            <FilterSelect
              value={filtroTipoCarga}
              onChange={(e) => setFiltroTipoCarga(e.target.value)}
            >
              <option value="">Todos los tipos</option>
              {opcionesTiposCarga.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>

        <FilterActions>
          <FilterButton variant="secondary" onClick={limpiarFiltros}>
            <X size={18} />
            Limpiar Filtros
          </FilterButton>
        </FilterActions>
      </FiltersSection>

      {/* Estadísticas Principales */}
      <StatsGrid>
        <StatCard color="#3b82f6">
          <StatHeader>
            <StatIcon color="#3b82f6">
              <Database size={28} />
            </StatIcon>
          </StatHeader>
          <StatContent>
            <StatValue>{estadisticas.total_rutas.toLocaleString()}</StatValue>
            <StatLabel>Total de Rutas</StatLabel>
            <StatSubtext>Registros disponibles</StatSubtext>
          </StatContent>
        </StatCard>

        <StatCard color="#10b981">
          <StatHeader>
            <StatIcon color="#10b981">
              <Users size={28} />
            </StatIcon>
          </StatHeader>
          <StatContent>
            <StatValue>{estadisticas.total_oferentes.toLocaleString()}</StatValue>
            <StatLabel>Oferentes Registrados</StatLabel>
            <StatSubtext>Proveedores activos en el sistema</StatSubtext>
          </StatContent>
        </StatCard>

        <StatCard color="#f59e0b">
          <StatHeader>
            <StatIcon color="#f59e0b">
              <FileText size={28} />
            </StatIcon>
          </StatHeader>
          <StatContent>
            <StatValue>{estadisticas.total_ofertas.toLocaleString()}</StatValue>
            <StatLabel>Ofertas Diligenciadas</StatLabel>
            <StatSubtext>Propuestas enviadas por proveedores</StatSubtext>
          </StatContent>
        </StatCard>

        <StatCard color="#8b5cf6">
          <StatHeader>
            <StatIcon color="#8b5cf6">
              <TrendingUp size={28} />
            </StatIcon>
          </StatHeader>
          <StatContent>
            <StatValue>{estadisticas.porcentaje_cobertura}%</StatValue>
            <StatLabel>Cobertura de Ofertas</StatLabel>
            <StatSubtext>{estadisticas.rutas_con_ofertas} de {estadisticas.total_rutas} rutas</StatSubtext>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Contenido Principal */}
      <ContentGrid>
        {/* Proveedores más activos */}
        <Section>
          <SectionHeader>
            <Award size={20} color="#10b981" />
            <SectionTitle>Proveedores Más Activos</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {estadisticas.proveedores_activos.length > 0 ? (
              <List>
                {estadisticas.proveedores_activos.map((proveedor, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <ListItemLabel>#{index + 1} {proveedor.proveedor}</ListItemLabel>
                      <ListItemSubtext>Ofertas enviadas</ListItemSubtext>
                    </ListItemText>
                    <ListItemValue color="#10b981">{proveedor.ofertas}</ListItemValue>
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>No hay proveedores con ofertas</EmptyState>
            )}
          </SectionContent>
        </Section>

        {/* Rutas más cotizadas */}
        <Section>
          <SectionHeader>
            <Truck size={20} color="#3b82f6" />
            <SectionTitle>Rutas Más Cotizadas</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {estadisticas.rutas_mas_cotizadas.length > 0 ? (
              <List>
                {estadisticas.rutas_mas_cotizadas.map((ruta, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <ListItemLabel>{ruta.ruta}</ListItemLabel>
                      <ListItemSubtext>Proveedores interesados</ListItemSubtext>
                    </ListItemText>
                    <ListItemValue color="#3b82f6">{ruta.proveedores}</ListItemValue>
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>No hay rutas cotizadas</EmptyState>
            )}
          </SectionContent>
        </Section>

        {/* Distribución por tipo de vehículo */}
        <Section>
          <SectionHeader>
            <Truck size={20} color="#f59e0b" />
            <SectionTitle>Distribución por Vehículo</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {estadisticas.distribucion_vehiculos.length > 0 ? (
              <List>
                {estadisticas.distribucion_vehiculos.map((vehiculo, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <ListItemLabel>{vehiculo.tipo}</ListItemLabel>
                      <ListItemSubtext>Ofertas recibidas</ListItemSubtext>
                    </ListItemText>
                    <ListItemValue color="#f59e0b">{vehiculo.total}</ListItemValue>
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>Sin distribución</EmptyState>
            )}
          </SectionContent>
        </Section>

        {/* Rutas sin ofertas */}
        <Section>
          <SectionHeader>
            <AlertTriangle size={20} color="#ef4444" />
            <SectionTitle>Rutas Sin Ofertas</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {estadisticas.rutas_sin_ofertas.length > 0 ? (
              <List>
                {estadisticas.rutas_sin_ofertas.map((ruta, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <ListItemLabel>{ruta.ruta}</ListItemLabel>
                      <ListItemSubtext>
                        {ruta.tipo_vehiculo} • {ruta.tipo_carga}
                      </ListItemSubtext>
                    </ListItemText>
                    <ListItemValue color="#ef4444">
                      ${ruta.valor_sicetac.toLocaleString()}
                    </ListItemValue>
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>¡Todas las rutas tienen ofertas! 🎉</EmptyState>
            )}
          </SectionContent>
        </Section>
      </ContentGrid>

      {/* Ranking de Proveedores */}
      <AnalysisSection>
        <AnalysisHeader>
          <AnalysisTitle>
            <Award size={28} />
            🏆 Ranking de Proveedores
          </AnalysisTitle>
        </AnalysisHeader>

        {loadingRanking ? (
          <AnalysisContent>
            <LoadingSpinner>
              <Activity size={32} style={{ marginRight: '12px', animation: 'spin 2s linear infinite' }} />
              Cargando ranking...
            </LoadingSpinner>
          </AnalysisContent>
        ) : rankingProveedores.length > 0 ? (
          <AnalysisContent>
            {/* Destacados */}
            {destacados && (
              <DestacadosGrid>
                <DestacadoCard color="#10b981">
                  <h4>💚 Más Económico</h4>
                  <p>{destacados.mejor_proveedor || 'N/A'}</p>
                  <span style={{ fontSize: '12px', opacity: 0.8 }}>Menor costo por km</span>
                </DestacadoCard>
                <DestacadoCard color="#ef4444">
                  <h4>💰 Más Caro</h4>
                  <p>{destacados.mas_caro || 'N/A'}</p>
                  <span style={{ fontSize: '12px', opacity: 0.8 }}>Mayor costo por km</span>
                </DestacadoCard>
              </DestacadosGrid>
            )}

            {/* Tabla de ranking simplificada */}
            <div style={{ overflowX: 'auto' }}>
              <RankingTable>
                <TableHeader>
                  <tr>
                    <th>#</th>
                    <th>Proveedor</th>
                    <th>Ofertas</th>
                    <th>Costo por KM</th>
                    <th>Tipo</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {rankingProveedores.map((item, index) => (
                    <tr key={item.proveedor}>
                      <td>
                        <strong>{index + 1}</strong>
                        {index === 0 && ' 🥇'}
                        {index === 1 && ' 🥈'}
                        {index === 2 && ' 🥉'}
                      </td>
                      <td>
                        <strong>{item.proveedor}</strong>
                      </td>
                      <td>{item.ofertas || 0}</td>
                      <td>
                        <strong>${(item.promedio_oferta || 0).toLocaleString('es-CO')}</strong>
                      </td>
                      <td>
                        {index === 0 && <Badge color="#10b981">💚 Más Económico</Badge>}
                        {index === rankingProveedores.length - 1 && <Badge color="#ef4444">💰 Más Caro</Badge>}
                      </td>
                    </tr>
                  ))}
                </TableBody>
              </RankingTable>
            </div>

            <div style={{ marginTop: '20px', fontSize: '14px', color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
              <p>
                📊 Total de proveedores: <strong>{rankingProveedores.length}</strong> | 
                Ordenados por costo por kilómetro (menor a mayor = más económico primero)
              </p>
            </div>
          </AnalysisContent>
        ) : (
          <AnalysisContent>
            <EmptyState>
              <Award size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>No hay datos de proveedores disponibles</p>
            </EmptyState>
          </AnalysisContent>
        )}
      </AnalysisSection>

      {/* Análisis Avanzado con Gemini AI */}
      <AnalysisSection>
        <AnalysisHeader>
          <AnalysisTitle>
            <Sparkles size={28} />
            Análisis Estratégico con IA
          </AnalysisTitle>
          <GenerateButton 
            onClick={generarAnalisisGemini}
            disabled={loadingAnalisis}
          >
            {loadingAnalisis ? (
              <>
                <Activity size={18} style={{ animation: 'spin 2s linear infinite' }} />
                Generando...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Generar Análisis General
              </>
            )}
          </GenerateButton>
        </AnalysisHeader>

        {analisisGemini ? (
          <AnalysisContent>
            {/* Resumen Ejecutivo */}
            <AnalysisBlock>
              <AnalysisBlockTitle>
                <Target size={20} />
                Resumen Ejecutivo
              </AnalysisBlockTitle>
              <AnalysisText>{analisisGemini.resumen_ejecutivo}</AnalysisText>
            </AnalysisBlock>

            {/* Diagnóstico General */}
            {analisisGemini.diagnostico_general.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <Activity size={20} />
                  Diagnóstico General
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.diagnostico_general.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {/* Oportunidades */}
            {analisisGemini.oportunidades.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <Lightbulb size={20} />
                  Oportunidades Detectadas
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.oportunidades.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {/* Riesgos */}
            {analisisGemini.riesgos.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <Shield size={20} />
                  Riesgos Identificados
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.riesgos.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {/* Recomendaciones Estratégicas */}
            {analisisGemini.recomendaciones_estrategicas.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <Target size={20} />
                  Recomendaciones Estratégicas
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.recomendaciones_estrategicas.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {/* Insights Competitivos */}
            {analisisGemini.insights_competitivos.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <Award size={20} />
                  Insights Competitivos
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.insights_competitivos.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {/* Proyecciones */}
            {analisisGemini.proyecciones.length > 0 && (
              <AnalysisBlock>
                <AnalysisBlockTitle>
                  <TrendingUp size={20} />
                  Proyecciones
                </AnalysisBlockTitle>
                <AnalysisList>
                  {analisisGemini.proyecciones.map((item, index) => (
                    <AnalysisListItem key={index}>{item}</AnalysisListItem>
                  ))}
                </AnalysisList>
              </AnalysisBlock>
            )}

            {analisisTimestamp && (
              <AnalysisTimestamp>
                Análisis generado el {new Date(analisisTimestamp).toLocaleString('es-CO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </AnalysisTimestamp>
            )}
          </AnalysisContent>
        ) : (
          <AnalysisContent>
            <EmptyState style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              <Sparkles size={48} style={{ margin: '0 auto 16px', opacity: 0.6 }} />
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>
                Haz clic en "Generar Análisis" para obtener insights estratégicos profundos
              </p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>
                Powered by Gemini 2.5 Flash ✨
              </p>
            </EmptyState>
          </AnalysisContent>
        )}
      </AnalysisSection>

      {/* Sección de Proveedores que cumplen con los filtros */}
      {rankingProveedores.length > 0 && (
        <AnalysisSection>
          <AnalysisHeader>
            <AnalysisTitle>
              <Users size={28} />
              Proveedores que Cotizan {(filtroOrigenCiudad || filtroDestinoCiudad || filtroTipoVehiculo || filtroTipoCarga) ? 'en Rutas Filtradas' : 'en el Sistema'}
            </AnalysisTitle>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
              {rankingProveedores.length} proveedor{rankingProveedores.length !== 1 ? 'es' : ''} encontrado{rankingProveedores.length !== 1 ? 's' : ''}
            </div>
          </AnalysisHeader>
          <AnalysisContent>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {rankingProveedores.map((prov, index) => (
                <div
                  key={prov.proveedor}
                  onClick={() => obtenerInfoProveedor(prov.proveedor)}
                  style={{
                    padding: '16px',
                    background: filtroProveedor === prov.proveedor 
                      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)'
                      : 'rgba(255,255,255,0.05)',
                    border: filtroProveedor === prov.proveedor 
                      ? '2px solid rgba(59, 130, 246, 0.6)'
                      : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (filtroProveedor !== prov.proveedor) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filtroProveedor !== prov.proveedor) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1, fontWeight: 'bold', fontSize: '16px', color: '#fff' }}>
                      {prov.proveedor}
                    </div>
                    {filtroProveedor === prov.proveedor && (
                      <CheckCircle size={20} style={{ color: '#22c55e' }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    <div>
                      <strong>Ofertas:</strong> {prov.ofertas || 0}
                    </div>
                    <div>
                      <strong>Promedio:</strong> ${prov.promedio_oferta ? prov.promedio_oferta.toLocaleString('es-CO') : '0'}/km
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Información detallada del proveedor seleccionado */}
            {proveedorInfo && filtroProveedor && (
              <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '2px solid rgba(59, 130, 246, 0.3)' }}>
                <h3 style={{ fontSize: '24px', marginBottom: '24px', color: '#60a5fa', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Users size={24} />
                  Información Completa: {proveedorInfo.nombre}
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  
                  {/* Datos Generales */}
                  <AnalysisBlock>
                    <AnalysisBlockTitle>
                      <FileText size={20} />
                      Datos Generales
                    </AnalysisBlockTitle>
                    <AnalysisList>
                      <AnalysisListItem><strong>NIT:</strong> {proveedorInfo.nit}</AnalysisListItem>
                      <AnalysisListItem><strong>Usuario:</strong> {proveedorInfo.username}</AnalysisListItem>
                      <AnalysisListItem><strong>Estado:</strong> {proveedorInfo.estado}</AnalysisListItem>
                      <AnalysisListItem><strong>Contacto:</strong> {proveedorInfo.contacto.nombre || 'No especificado'}</AnalysisListItem>
                      <AnalysisListItem><strong>Email:</strong> {proveedorInfo.contacto.correo || 'No especificado'}</AnalysisListItem>
                      <AnalysisListItem><strong>Teléfono:</strong> {proveedorInfo.contacto.celular || 'No especificado'}</AnalysisListItem>
                    </AnalysisList>
                  </AnalysisBlock>

                  {/* Estadísticas de Cotización */}
                  <AnalysisBlock style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)' }}>
                    <AnalysisBlockTitle style={{ color: '#22c55e' }}>
                      <BarChart3 size={20} />
                      Estadísticas de Cotización
                    </AnalysisBlockTitle>
                    <AnalysisList>
                      <AnalysisListItem>
                        <strong>Rutas cotizadas:</strong> {proveedorInfo.estadisticas_cotizaciones.rutas_cotizadas}
                        {proveedorInfo.estadisticas_cotizaciones.rutas_cotizadas === 0 && (
                          <span style={{ color: '#ef4444', marginLeft: '8px' }}>⚠️ Sin cotizaciones</span>
                        )}
                      </AnalysisListItem>
                      <AnalysisListItem><strong>Total cotizaciones:</strong> {proveedorInfo.estadisticas_cotizaciones.total_cotizaciones}</AnalysisListItem>
                      {proveedorInfo.estadisticas_cotizaciones.promedio_oferta > 0 && (
                        <>
                          <AnalysisListItem><strong>Promedio oferta:</strong> ${proveedorInfo.estadisticas_cotizaciones.promedio_oferta.toLocaleString('es-CO')}</AnalysisListItem>
                          <AnalysisListItem><strong>Oferta mínima:</strong> ${proveedorInfo.estadisticas_cotizaciones.oferta_minima.toLocaleString('es-CO')}</AnalysisListItem>
                          <AnalysisListItem><strong>Oferta máxima:</strong> ${proveedorInfo.estadisticas_cotizaciones.oferta_maxima.toLocaleString('es-CO')}</AnalysisListItem>
                        </>
                      )}
                    </AnalysisList>
                  </AnalysisBlock>

                  {/* Experiencia */}
                  <AnalysisBlock>
                    <AnalysisBlockTitle>
                      <Award size={20} />
                      Experiencia
                    </AnalysisBlockTitle>
                    <AnalysisList>
                      <AnalysisListItem><strong>Años de experiencia:</strong> {proveedorInfo.experiencia.anios || 'No especificado'}</AnalysisListItem>
                      <AnalysisListItem>
                        <strong>Transporte de alimentos:</strong> {proveedorInfo.experiencia.transporte_alimentos ? '✅ Sí' : '❌ No'}
                      </AnalysisListItem>
                      <AnalysisListItem>
                        <strong>Ha trabajado con Alianza:</strong> {proveedorInfo.experiencia.ha_trabajado_alianza ? '✅ Sí' : '❌ No'}
                      </AnalysisListItem>
                    </AnalysisList>
                  </AnalysisBlock>

                  {/* Flota */}
                  <AnalysisBlock>
                    <AnalysisBlockTitle>
                      <Truck size={20} />
                      Flota
                    </AnalysisBlockTitle>
                    <AnalysisList>
                      <AnalysisListItem><strong>Número de vehículos:</strong> {proveedorInfo.flota.numero_vehiculos}</AnalysisListItem>
                      <AnalysisListItem><strong>% Flota propia:</strong> {proveedorInfo.flota.porcentaje_propia || 'No especificado'}</AnalysisListItem>
                      <AnalysisListItem><strong>Edad promedio:</strong> {proveedorInfo.flota.edad_promedio || 'No especificado'}</AnalysisListItem>
                      {proveedorInfo.flota.tecnologias && (
                        <AnalysisListItem><strong>Tecnologías:</strong> {proveedorInfo.flota.tecnologias}</AnalysisListItem>
                      )}
                      {proveedorInfo.flota.capacidades_carga && (
                        <AnalysisListItem><strong>Capacidades:</strong> {proveedorInfo.flota.capacidades_carga}</AnalysisListItem>
                      )}
                    </AnalysisList>
                  </AnalysisBlock>

                  {/* Tecnología */}
                  <AnalysisBlock>
                    <AnalysisBlockTitle>
                      <Activity size={20} />
                      Tecnología
                    </AnalysisBlockTitle>
                    <AnalysisList>
                      <AnalysisListItem><strong>Sistema GPS:</strong> {proveedorInfo.tecnologia.gps_sistema || 'No especificado'}</AnalysisListItem>
                      <AnalysisListItem>
                        <strong>Capacitación conductores:</strong> {proveedorInfo.tecnologia.capacitacion_conductores ? '✅ Sí' : '❌ No'}
                      </AnalysisListItem>
                      <AnalysisListItem>
                        <strong>Capacitación carga alimentos:</strong> {proveedorInfo.tecnologia.capacitacion_carga_alimentos ? '✅ Sí' : '❌ No'}
                      </AnalysisListItem>
                      <AnalysisListItem>
                        <strong>Medición huella de carbono:</strong> {proveedorInfo.tecnologia.medicion_huella_carbono ? '✅ Sí' : '❌ No'}
                      </AnalysisListItem>
                    </AnalysisList>
                  </AnalysisBlock>

                  {/* Última Milla */}
                  {proveedorInfo.ultima_milla.tiene_capacidad && (
                    <AnalysisBlock>
                      <AnalysisBlockTitle>
                        <Package size={20} />
                        Última Milla
                      </AnalysisBlockTitle>
                      <AnalysisList>
                        <AnalysisListItem><strong>Capacidad:</strong> ✅ Sí</AnalysisListItem>
                        {proveedorInfo.ultima_milla.ciudades && (
                          <AnalysisListItem><strong>Ciudades:</strong> {proveedorInfo.ultima_milla.ciudades}</AnalysisListItem>
                        )}
                      </AnalysisList>
                    </AnalysisBlock>
                  )}

                </div>

                {/* Certificaciones (si existen) */}
                {proveedorInfo.certificaciones && (
                  <AnalysisBlock style={{ marginTop: '20px' }}>
                    <AnalysisBlockTitle>
                      <CheckCircle size={20} />
                      Certificaciones
                    </AnalysisBlockTitle>
                    <AnalysisText>{proveedorInfo.certificaciones}</AnalysisText>
                  </AnalysisBlock>
                )}

                {/* Botón para generar análisis con IA */}
                <div style={{ marginTop: '32px', textAlign: 'center' }}>
                  <GenerateButton 
                    onClick={generarAnalisisProveedor}
                    disabled={loadingAnalisisProveedor}
                  >
                    {loadingAnalisisProveedor ? (
                      <>
                        <Activity size={18} style={{ animation: 'spin 2s linear infinite' }} />
                        Generando Análisis...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        {analisisProveedorTexto ? 'Regenerar Análisis con IA' : 'Generar Análisis con IA'}
                      </>
                    )}
                  </GenerateButton>
                </div>

                {/* Análisis del proveedor con IA */}
                {analisisProveedorTexto && (
                  <AnalysisBlock style={{ 
                    marginTop: '24px', 
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)',
                    border: '2px solid rgba(59, 130, 246, 0.3)'
                  }}>
                    <AnalysisBlockTitle style={{ color: '#60a5fa', fontSize: '20px', marginBottom: '16px' }}>
                      <Sparkles size={24} />
                      Análisis Profesional con IA
                    </AnalysisBlockTitle>
                    <div style={{ 
                      fontSize: '15px', 
                      lineHeight: '1.8', 
                      color: 'rgba(255,255,255,0.95)',
                      textAlign: 'justify',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {analisisProveedorTexto}
                    </div>
                    <div style={{ 
                      marginTop: '20px', 
                      padding: '12px', 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.7)',
                      textAlign: 'center'
                    }}>
                      ✨ Análisis generado con Gemini 2.0 Flash
                    </div>
                  </AnalysisBlock>
                )}
              </div>
            )}
          </AnalysisContent>
        </AnalysisSection>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
